export const TENANT_BACKEND_API_KEY_GATE = 'fe-1416-tenant-backend-api-key'

export const API_CREDENTIALS_TABLE = 'api_credentials'

export const READ_VAULT_SECRET_RPC = 'read_vault_secret'

export const TenantBackendApiKeyErrorCode = {
  MISSING_TENANT_ID: 'MISSING_TENANT_ID',
  MISSING_LANDING_ZONE_CLIENT: 'MISSING_LANDING_ZONE_CLIENT',
  MISSING_COMMAND_CENTER_CLIENT: 'MISSING_COMMAND_CENTER_CLIENT',
  CREDENTIAL_LOOKUP_FAILED: 'CREDENTIAL_LOOKUP_FAILED',
  MISSING_TENANT_CREDENTIAL: 'MISSING_TENANT_CREDENTIAL',
  SECRET_RETRIEVAL_FAILED: 'SECRET_RETRIEVAL_FAILED',
  MISSING_ENV_API_KEY: 'MISSING_ENV_API_KEY',
}

export class TenantBackendApiKeyError extends Error {
  /**
   * @param {string} code
   * @param {string} message
   */
  constructor(code, message) {
    super(message)
    this.name = 'TenantBackendApiKeyError'
    this.code = code
  }
}

const apiKeyCache = new Map()
const apiKeyInflight = new Map()

/**
 * @returns {string} Tenant id from hydrated tenant data or local storage.
 */
export function getTenantIdFromContext() {
  const hydratedData = typeof window !== 'undefined' ? window.__TENANT_DATA__ : null
  const tenantId = hydratedData?.tenant_id || window.localStorage?.getItem('tenantId') || ''
  return String(tenantId || '').trim()
}

/**
 * @param {unknown} data
 * @returns {string} Normalized secret value from RPC payload.
 */
export function normalizeVaultSecretResult(data) {
  if (typeof data === 'string') return data.trim()
  if (!data || typeof data !== 'object') return ''

  const candidates = [data.api_key, data.apiKey, data.secret, data.secret_key, data.value]

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }

  return ''
}

export function clearTenantBackendApiKeyCache() {
  apiKeyCache.clear()
  apiKeyInflight.clear()
}

/**
 * Resolves the tenant APIM API key:
 * 1. Lookup `apim_secret_name` in Landing Zone `api_credentials` by `tenant_id`
 * 2. Read the secret via Command Center `read_vault_secret(secret_name)`
 *
 * @param {object} params
 * @param {import('@supabase/supabase-js').SupabaseClient} params.landingZoneClient
 * @param {import('@supabase/supabase-js').SupabaseClient} params.commandCenterClient
 * @param {string} params.tenantId
 */
export async function resolveTenantBackendApiKey({
  landingZoneClient,
  commandCenterClient,
  tenantId,
}) {
  const resolvedTenantId = String(tenantId || '').trim()
  if (!resolvedTenantId) {
    throw new TenantBackendApiKeyError(
      TenantBackendApiKeyErrorCode.MISSING_TENANT_ID,
      'Tenant context is required for backend API key resolution.',
    )
  }

  if (!landingZoneClient) {
    throw new TenantBackendApiKeyError(
      TenantBackendApiKeyErrorCode.MISSING_LANDING_ZONE_CLIENT,
      'Landing Zone client is required for backend API key resolution.',
    )
  }

  if (!commandCenterClient) {
    throw new TenantBackendApiKeyError(
      TenantBackendApiKeyErrorCode.MISSING_COMMAND_CENTER_CLIENT,
      'Command Center client is required for backend API key resolution.',
    )
  }

  if (apiKeyCache.has(resolvedTenantId)) {
    return apiKeyCache.get(resolvedTenantId)
  }

  if (apiKeyInflight.has(resolvedTenantId)) {
    return apiKeyInflight.get(resolvedTenantId)
  }

  const resolvePromise = (async () => {
    const { data: credentialRow, error: credentialError } = await landingZoneClient
      .from(API_CREDENTIALS_TABLE)
      .select('apim_secret_name')
      .eq('tenant_id', resolvedTenantId)
      .maybeSingle()

    if (credentialError) {
      throw new TenantBackendApiKeyError(
        TenantBackendApiKeyErrorCode.CREDENTIAL_LOOKUP_FAILED,
        `Failed to fetch tenant backend credentials: ${credentialError.message}`,
      )
    }

    const secretName = String(credentialRow?.apim_secret_name || '').trim()
    if (!secretName) {
      throw new TenantBackendApiKeyError(
        TenantBackendApiKeyErrorCode.MISSING_TENANT_CREDENTIAL,
        'No backend API credential is configured for this tenant.',
      )
    }

    const { data, error } = await commandCenterClient.rpc(READ_VAULT_SECRET_RPC, {
      secret_name: secretName,
    })

    if (error) {
      throw new TenantBackendApiKeyError(
        TenantBackendApiKeyErrorCode.SECRET_RETRIEVAL_FAILED,
        `Failed to retrieve backend API key: ${error.message}`,
      )
    }

    const apiKey = normalizeVaultSecretResult(data)
    if (!apiKey) {
      throw new TenantBackendApiKeyError(
        TenantBackendApiKeyErrorCode.SECRET_RETRIEVAL_FAILED,
        'Backend API key secret is empty.',
      )
    }

    apiKeyCache.set(resolvedTenantId, apiKey)
    return apiKey
  })()

  apiKeyInflight.set(resolvedTenantId, resolvePromise)

  try {
    return await resolvePromise
  } finally {
    apiKeyInflight.delete(resolvedTenantId)
  }
}

/**
 * @param {object} params
 * @param {import('@supabase/supabase-js').SupabaseClient} [params.landingZoneClient]
 * @param {import('@supabase/supabase-js').SupabaseClient} [params.commandCenterClient]
 * @param {string} [params.tenantId]
 * @param {boolean} [params.useTenantBackendApiKey]
 */
export async function resolveContxtBackendApiKey({
  landingZoneClient,
  commandCenterClient,
  tenantId,
  useTenantBackendApiKey = false,
} = {}) {
  if (useTenantBackendApiKey && landingZoneClient && commandCenterClient) {
    return resolveTenantBackendApiKey({
      landingZoneClient,
      commandCenterClient,
      tenantId: tenantId || getTenantIdFromContext(),
    })
  }

  const envApiKey = import.meta.env.VITE_CONTXT_API_KEY
  if (!envApiKey) {
    throw new TenantBackendApiKeyError(
      TenantBackendApiKeyErrorCode.MISSING_ENV_API_KEY,
      'Backend API key is not configured.',
    )
  }

  return envApiKey
}
